import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTL } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, Printer } from "lucide-react";
import { recalcItem, isSteelType, steelTypeOptions, SteelDimsEditor } from "@/lib/steelCalc";

const UNIT_OPTIONS = ["adet", "kg", "kilogram", "ton", "metre", "boy", "levha", "plaka", "paket", "kutu"];
const TEVKIFAT_RATES = ["2/10", "3/10", "4/10", "5/10", "7/10", "9/10"];
const parseRate = (r) => { const [a, b] = (r || "0/10").split("/").map(Number); return b ? a / b : 0; };

export default function QuoteForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ default_vat: 20, quote_validity_days: 7, default_payment_terms: "", default_delivery_terms: "", default_quote_terms: "" });
  const [form, setForm] = useState({
    customer_id: "",
    date: new Date().toISOString().slice(0, 10),
    valid_until: "",
    currency: "TRY",
    payment_terms: "",
    delivery_terms: "",
    description: "",
    note: "",
    items: [],
    discount: 0,
    tevkifat_enabled: false,
    tevkifat_rate: "7/10",
    status: "taslak",
  });
  const [number, setNumber] = useState("");

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings/company").then((r) => {
      setSettings(r.data);
      setForm((f) => ({
        ...f,
        payment_terms: f.payment_terms || r.data.default_payment_terms || "",
        delivery_terms: f.delivery_terms || r.data.default_delivery_terms || "",
        description: f.description || r.data.default_quote_terms || "",
        valid_until: f.valid_until || new Date(Date.now() + (r.data.quote_validity_days || 7) * 86400000).toISOString().slice(0, 10),
      }));
    });
    if (isEdit) {
      api.get(`/quotes/${id}`).then((r) => {
        setForm({
          customer_id: r.data.customer_id,
          date: r.data.date?.slice(0, 10) || "",
          valid_until: r.data.valid_until?.slice(0, 10) || "",
          currency: r.data.currency || "TRY",
          payment_terms: r.data.payment_terms || "",
          delivery_terms: r.data.delivery_terms || "",
          description: r.data.description || "",
          note: r.data.note || "",
          items: r.data.items || [],
          discount: r.data.discount || 0,
          tevkifat_enabled: r.data.tevkifat_enabled || false,
          tevkifat_rate: r.data.tevkifat_rate || "7/10",
          status: r.data.status || "taslak",
        });
        setNumber(r.data.number);
      });
    }
  }, [id]);

  const addRow = () => setForm({
    ...form,
    items: [...form.items, { product_id: "", product_name: "", description: "", size: "", quantity: 1, unit: "kg", unit_price: 0, discount: 0, vat_rate: settings.default_vat || 20, total: 0, calc_type: "", thickness: 0, width: 0, height: 0, length: 0, unit_kg: 0, total_kg: 0, net_kg: 0, fire_pct: 0, note: "" }],
  });

  const setRow = (i, patch) => {
    const items = [...form.items];
    items[i] = recalcItem({ ...items[i], ...patch });
    setForm({ ...form, items });
  };

  const rmRow = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  const subtotal = form.items.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const vat_amount = form.items.reduce((s, i) => s + ((Number(i.total) || 0) * (Number(i.vat_rate) || 0) / 100), 0);
  const tevkifat_amount = form.tevkifat_enabled ? vat_amount * parseRate(form.tevkifat_rate) : 0;
  const payable_vat = vat_amount - tevkifat_amount;
  const total = subtotal + payable_vat - (Number(form.discount) || 0);

  const [saving, setSaving] = useState(false);
  const submit = async (openPrint = false) => {
    if (saving) return;
    if (!form.customer_id) return toast.error("Müşteri seçin");
    if (form.items.length === 0) return toast.error("En az bir ürün ekleyin");
    if (form.items.some((it) => !it.product_name?.trim())) return toast.error("Ürün adları girin");
    if (form.items.some((it) => isSteelType(it.calc_type) && !(Number(it.unit_kg) > 0))) return toast.error("Otomatik kg satırları için tüm ölçüleri (mm) girin");
    const body = { ...form, subtotal, vat_amount, tevkifat_amount, total, discount: Number(form.discount) || 0 };
    setSaving(true);
    try {
      let qid = id;
      if (isEdit) {
        await api.put(`/quotes/${id}`, body);
        toast.success("Teklif güncellendi");
      } else {
        const { data } = await api.post("/quotes", body);
        qid = data.id;
        toast.success("Teklif oluşturuldu: " + data.number);
      }
      if (openPrint) {
        window.open(`/teklifler/${qid}/yazdir`, "_blank");
      }
      nav("/teklifler");
    } catch (e) { if (e.response && e.response.status < 500) toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="quote-form-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)}><ArrowLeft size={18} /></Button>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">{isEdit ? `Teklif Düzenle` : "Yeni Teklif"}</h1>
            {number && <p className="text-sm text-muted-foreground font-mono">{number}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => submit(true)} disabled={saving} data-testid="quote-save-print-btn"><Printer size={16} className="mr-1" /> Kaydet ve Yazdır</Button>
          <Button onClick={() => submit(false)} disabled={saving} data-testid="quote-save-btn"><Save size={16} className="mr-1" /> {saving ? "Kaydediliyor..." : "Kaydet"}</Button>
        </div>
      </div>

      <Card className="p-5 space-y-4">
        <h3 className="font-bold text-lg">Teklif Bilgileri</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Müşteri *</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
              <SelectContent className="bg-popover max-h-96">
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Teklif Tarihi</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><Label>Geçerlilik Tarihi</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">Ürünler / Hizmetler</h3>
          <Button size="sm" variant="outline" onClick={addRow} data-testid="add-quote-item-btn"><Plus size={14} className="mr-1" /> Satır Ekle</Button>
        </div>
        <datalist id="quote-product-list">
          {products.map((p) => <option key={p.id} value={`${p.name}${p.size ? " · " + p.size : ""}`} />)}
        </datalist>

        <div className="border border-border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2 w-12">Sıra</th>
                <th className="text-left p-2">Ürün / Açıklama</th>
                <th className="text-left p-2">Hesaplama</th>
                <th className="text-right p-2">Miktar</th>
                <th className="text-left p-2">Birim</th>
                <th className="text-right p-2">Birim Fiyat</th>
                <th className="text-right p-2">İskonto</th>
                <th className="text-right p-2">KDV %</th>
                <th className="text-right p-2">Toplam</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const isAuto = isSteelType(it.calc_type);
                return (
                  <React.Fragment key={i}>
                    <tr className="border-t border-border">
                      <td className="p-1.5 text-center num text-muted-foreground">{i + 1}</td>
                      <td className="p-1.5 min-w-[220px]">
                        <Input list="quote-product-list" value={it.product_name} placeholder="Ürün adı yazın"
                          onChange={(e) => {
                            const val = e.target.value;
                            const match = products.find((p) => `${p.name}${p.size ? " · " + p.size : ""}` === val || p.name === val);
                            if (match) setRow(i, { product_id: match.id, product_name: match.name, size: match.size || "", unit: match.unit || "kg", unit_price: match.sale_price });
                            else setRow(i, { product_id: "", product_name: val });
                          }}
                          className="h-9" data-testid={`quote-product-input-${i}`} />
                      </td>
                      <td className="p-1.5 min-w-[150px]">
                        <Select value={it.calc_type || "normal"} onValueChange={(v) => setRow(i, { calc_type: v === "normal" ? "" : v })}>
                          <SelectTrigger className="h-9" data-testid={`quote-calc-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-popover">
                            <SelectItem value="normal">Normal</SelectItem>
                            {steelTypeOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1.5 text-right">
                        {isAuto
                          ? <span className="num text-muted-foreground" data-testid={`quote-qty-ro-${i}`}>{Number(it.total_kg || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg</span>
                          : <Input type="number" step="0.01" value={it.quantity} onChange={(e) => setRow(i, { quantity: Number(e.target.value) })} className="h-9 text-right w-20" />}
                      </td>
                      <td className="p-1.5">
                        {isAuto
                          ? <span className="text-muted-foreground">kg</span>
                          : <Input list="quote-unit-list" value={it.unit} onChange={(e) => setRow(i, { unit: e.target.value })} className="h-9 w-24" />}
                      </td>
                      <td className="p-1.5 text-right">
                        {isAuto
                          ? <span className="num text-muted-foreground">{Number(it.unit_price || 0).toLocaleString("tr-TR")} ₺/kg</span>
                          : <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setRow(i, { unit_price: Number(e.target.value) })} className="h-9 text-right w-24" />}
                      </td>
                      <td className="p-1.5"><Input type="number" step="0.01" value={it.discount} onChange={(e) => setRow(i, { discount: Number(e.target.value) })} className="h-9 text-right w-20" /></td>
                      <td className="p-1.5"><Input type="number" step="1" value={it.vat_rate} onChange={(e) => setRow(i, { vat_rate: Number(e.target.value) })} className="h-9 text-right w-16" /></td>
                      <td className="p-1.5 text-right num font-semibold">{fmtTL(it.total)}</td>
                      <td className="p-1.5"><Button variant="ghost" size="icon" onClick={() => rmRow(i)}><Trash2 size={14} /></Button></td>
                    </tr>
                    {isAuto && (
                      <tr className="bg-muted/10">
                        <td></td>
                        <td colSpan={9} className="p-2">
                          <SteelDimsEditor item={it} onPatch={(patch) => setRow(i, patch)} idPrefix={`quote-${i}`} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {form.items.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Ürün eklenmedi</td></tr>}
            </tbody>
          </table>
          <datalist id="quote-unit-list">
            {UNIT_OPTIONS.map((u) => <option key={u} value={u} />)}
          </datalist>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div><Label>Ödeme Şartları</Label><Textarea rows={2} value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></div>
            <div><Label>Teslimat Şartları</Label><Textarea rows={2} value={form.delivery_terms} onChange={(e) => setForm({ ...form, delivery_terms: e.target.value })} /></div>
            <div><Label>Açıklama</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Not (dahili)</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <div className="bg-muted/40 rounded-lg p-5 space-y-3 h-fit">
            <div className="flex justify-between text-sm"><span>Ara Toplam</span><span className="num">{fmtTL(subtotal)}</span></div>
            <div className="flex justify-between text-sm items-center">
              <span>Genel İskonto</span>
              <Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="h-8 w-28 text-right" />
            </div>
            <div className="flex justify-between text-sm"><span>KDV</span><span className="num">{fmtTL(vat_amount)}</span></div>

            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Switch checked={form.tevkifat_enabled} onCheckedChange={(v) => setForm({ ...form, tevkifat_enabled: v })} data-testid="tevkifat-switch" />
                  <Label className="cursor-pointer text-sm font-semibold">KDV Tevkifatı Uygula</Label>
                </div>
                {form.tevkifat_enabled && (
                  <Select value={form.tevkifat_rate} onValueChange={(v) => setForm({ ...form, tevkifat_rate: v })}>
                    <SelectTrigger className="h-8 w-24" data-testid="tevkifat-rate-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      {TEVKIFAT_RATES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {form.tevkifat_enabled && (
                <>
                  <div className="flex justify-between text-sm text-amber-500">
                    <span>Tevkifat ({form.tevkifat_rate})</span>
                    <span className="num">-{fmtTL(tevkifat_amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-muted-foreground">Ödenecek KDV</span>
                    <span className="num font-semibold">{fmtTL(payable_vat)}</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-between text-xl font-bold border-t border-border pt-3">
              <span>GENEL TOPLAM</span>
              <span className="num text-primary" data-testid="quote-total">{fmtTL(total)}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
